import styled from 'styled-components';
import { EmptyStateBox, Flex } from '@elementary/common-ui/components';
import { useMutation } from '@tanstack/react-query';
import useEnvironments from 'shared/hooks/useEnvironments';
import useSnackbar from 'shared/hooks/useSnackbar';
import useSupportChat from 'shared/hooks/useSupportChat';
import * as api from 'shared/services/settingsApi';

const Wrapper = styled(Flex)`
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const dwhLinkMap: Record<string, string> = {
  snowflake:
    'https://docs.elementary-data.com/cloud/integrations/dwh/snowflake',
  bigquery: 'https://docs.elementary-data.com/cloud/integrations/dwh/bigquery',
  redshift: 'https://docs.elementary-data.com/cloud/integrations/dwh/redshift',
  databricks:
    'https://docs.elementary-data.com/cloud/integrations/dwh/databricks',
  postgres: 'https://docs.elementary-data.com/cloud/integrations/dwh/postgres',
  athena: 'https://docs.elementary-data.com/cloud/integrations/dwh/athena',
  clickhouse:
    'https://docs.elementary-data.com/cloud/integrations/dwh/clickhouse',
  fabric: 'https://docs.elementary-data.com/cloud/integrations/dwh/fabric',
};

const ReportEmptyStatePage = () => {
  const { selectedEnv } = useEnvironments();
  const { openSnackbar } = useSnackbar();
  const supportChat = useSupportChat();

  const { mutate: syncNowMutate } = useMutation(
    () => api.syncNow(selectedEnv?.id || ''),
    {
      onSuccess: () => {
        openSnackbar({
          message: 'Sync triggered successfully!',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to trigger sync',
          variant: 'error',
        });
      },
    },
  );

  return (
    <Wrapper>
      <EmptyStateBox title="No results">
        Please make sure Elementary user has{' '}
        <EmptyStateBox.Link
          href={
            selectedEnv?.dwhConnection.warehouseType
              ? dwhLinkMap[selectedEnv.dwhConnection.warehouseType]
              : ''
          }
          target="_blank"
        >
          sufficient permissions
        </EmptyStateBox.Link>
        <br />
        and that dbt was invoked recently in this environment.
        <br />
        <br />
        <EmptyStateBox.SubTitle>
          Did you update permissions or invoke dbt?
        </EmptyStateBox.SubTitle>
        <br />
        <EmptyStateBox.ClickableText onClick={() => syncNowMutate()}>
          Run sync
        </EmptyStateBox.ClickableText>{' '}
        to update this environment.
        <br />
        <br />
        Still empty?{' '}
        <EmptyStateBox.ClickableText onClick={supportChat.show}>
          Contact us
        </EmptyStateBox.ClickableText>{' '}
        or{' '}
        <EmptyStateBox.Link
          href="https://docs.elementary-data.com/cloud/guides/troubleshoot#i-connected-my-data-warehouse-but-i-dont-see-any-test-results"
          target="_blank"
        >
          learn more here.
        </EmptyStateBox.Link>
      </EmptyStateBox>
    </Wrapper>
  );
};

export default ReportEmptyStatePage;
